import './styles.sass';

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import AddSourceDropdown from './AddSourceDropdown';

import AutocompleteField from 'components/Explorer/AutocompleteField';
import AutocompleteFieldConfig from 'components/Explorer/models/autocomplete_field_config';
import FilterSet from 'components/Explorer/models/filter_set';
import useFilterLookup from 'hooks/useFilterLookup';

const QUERY_FILTER_PATHS = {
  mentionSourcesForFilter: 'filters/mention_sources',
  mentionSourcesTypesForFilter: 'filters/mention_sources_types'
};

const MentionSourcesFilter = ({
  sourcesTypes,
  filterSet,
  queryFilter,
  assignRef,
  disabled = false,
  dropdownSourcesType,
  autocompleteFieldConfig
}) => {
  const autocompleteFieldRef = useRef(null);

  const hasInitialSelection = filterSet.get(autocompleteFieldConfig.name).length > 0;
  const { data, loading } = useFilterLookup(QUERY_FILTER_PATHS[queryFilter], { enabled: hasInitialSelection });
  const mentionSources = data ?? [];

  const sourceFromType = (sourceType) => {
    const source = {
      id: `type:${sourceType}`,
      type: dropdownSourcesType
    };
    if (dropdownSourcesType === 'post_type') source.post_type = sourceType;
    if (dropdownSourcesType === 'mention_source_type') source.mention_source_type = sourceType;
    return autocompleteFieldRef.current.modelizeResult(source);
  };

  const updateSelectedMentionSources = (selected) => {
    Analytics.trackEvent('mentions-update-sources');
    filterSet.set(
      autocompleteFieldConfig.name,
      selected.map((source) => source.id)
    );
  };

  // Expose an imperative API to the parent. The parent stores it on a ref and
  // calls clear/overrideSelectedMentionSourcesFromTypes/clearSelected on it.
  useEffect(() => {
    assignRef({
      clear() {
        autocompleteFieldRef.current?.clearAll();
        updateSelectedMentionSources([]);
      },
      overrideSelectedMentionSourcesFromTypes(sourceTypes) {
        if (!autocompleteFieldRef.current) return;
        const sources = sourceTypes.map((sourceType) => sourceFromType(sourceType));
        autocompleteFieldRef.current.overrideSelectedResults(sources);
      },
      clearSelected() {
        autocompleteFieldRef.current?.clearAll();
      }
    });
  }, []);

  const addSelectedSource = (sourceType) => {
    const mentionSource = sourceFromType(sourceType);
    autocompleteFieldRef.current.selectResult(mentionSource);
  };

  return (
    <div className="Explorer-FilterPanel-field Explorer-MentionSourcesField">
      {loading ? (
        <div>{I18n.t('Explorer.Mentions.FilterPanel.fields.mention_sources.loading')}</div>
      ) : (
        <div className="Explorer-MentionSourcesField-wrapper">
          <AddSourceDropdown sourcesTypes={sourcesTypes} addSelectedSource={addSelectedSource} disabled={disabled} />
          {disabled ? (
            <div className="FakeAutocompleteField" data-testid="fake-autocomplete"></div>
          ) : (
            <AutocompleteField
              id={autocompleteFieldConfig.id}
              name={autocompleteFieldConfig.name}
              type={autocompleteFieldConfig.type}
              placeholder={autocompleteFieldConfig.placeholder}
              onChangeItems={updateSelectedMentionSources}
              initiallySelected={mentionSources}
              ref={autocompleteFieldRef}
              delayShortQuery={true}
            />
          )}
        </div>
      )}
    </div>
  );
};

MentionSourcesFilter.propTypes = {
  sourcesTypes: PropTypes.object.isRequired,
  filterSet: PropTypes.instanceOf(FilterSet).isRequired,
  queryFilter: PropTypes.string.isRequired,
  assignRef: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  dropdownSourcesType: PropTypes.string.isRequired,
  autocompleteFieldConfig: PropTypes.instanceOf(AutocompleteFieldConfig).isRequired
};

export default MentionSourcesFilter;
